<template>
  <div>
    <!-- Incoming Call -->
    <section class="call_container py_12">
      <button
        v-if="isShowCloseBtn"
        class="close-button"
        @click="$emit('closeCall')"
      >
        <fluent-icon icon="dismiss" size="16" />
      </button>
      <div>
        <p class="">
          {{ callStatusText }}{{ ' - ' }}
          <b class="sub-block-title">
            {{ contactName || phoneNumber }}
          </b>
        </p>
      </div>
      <div>
        <h1>{{ callDurationLabel }}</h1>
      </div>
      <div v-if="callStatus.label" class="mb_12">
        <Label :color-scheme="callStatus.color" class="capitalize">
          {{ callStatus.label }}
          <span v-if="isDialing" class="animated-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </Label>
      </div>
      <div>
        <woot-button
          v-if="isAcceptBtnShow"
          v-tooltip="$t('CONTACT_PANEL.MUTE_CONTACT')"
          variant="smooth"
          color-scheme="success"
          size="small"
          :is-loading="isAccepting"
          :disabled="isAccepting"
          @click="$emit('acceptCall')"
        >
          <span v-if="!isAccepting">{{
            $t('CONTACT_PANEL.CALL.BTNS.ACCEPT')
          }}</span>
          <span v-else>
            {{ $t('CONTACT_PANEL.CALL.BTNS.ACCEPTING') }}
          </span>
        </woot-button>
        <woot-button
          v-if="isRejectBtnShow"
          v-tooltip="$t('CONTACT_PANEL.REJECT_CALL')"
          variant="smooth"
          color-scheme="alert"
          size="small"
          :is-loading="isRejecting"
          :disabled="isRejecting"
          @click="$emit('rejectCall')"
        >
          <span v-if="!isRejecting">{{
            $t('CONTACT_PANEL.CALL.BTNS.REJECT')
          }}</span>
          <span v-else>
            {{ $t('CONTACT_PANEL.CALL.BTNS.REJECTING') }}
          </span>
        </woot-button>
        <woot-button
          v-if="isRedialBtnShow"
          v-tooltip="$t('CONTACT_PANEL.REDIAL_CALL')"
          variant="smooth"
          color-scheme="success"
          size="small"
          :is-loading="isRedialing"
          :disabled="isRedialing"
          @click="$emit('redialCall')"
        >
          <span v-if="!isRedialing">{{
            $t('CONTACT_PANEL.CALL.BTNS.REDIAL')
          }}</span>
          <span v-else>{{ $t('CONTACT_PANEL.CALL.BTNS.REDIALING') }}</span>
        </woot-button>
        <woot-button
          v-if="isEndBtnShow"
          v-tooltip="$t('CONTACT_PANEL.END_CALL')"
          variant="smooth"
          color-scheme="alert"
          size="small"
          @click="$emit('endCall')"
        >
          {{ $t('CONTACT_PANEL.CALL.BTNS.END') }}
        </woot-button>
      </div>

      <div v-if="isShowEvaluationOptions" class="status_box">
        <MultiselectDropdown :label="$t('CONTACT_PANEL.CALL.EVALUATION.LABEL')">
          <select
            id="status"
            v-model="selectedStatus.id"
            class="paragraph paragraph--black"
            @change="statusChangeHandle"
          >
            <option disabled value="">
              {{ $t('CONTACT_PANEL.CALL.EVALUATION.PLACEHOLDER') }}
            </option>
            <option
              v-for="status in statusList"
              :key="status.id"
              :value="status.id"
              class="paragraph paragraph--black"
            >
              {{ status.title }}
            </option>
          </select>
        </MultiselectDropdown>
      </div>
    </section>
  </div>
</template>

<script>
import Label from 'dashboard/components/ui/Label.vue';
import MultiselectDropdown from 'components/ui/inputs/MultiselectDropdown.vue';
import { CALL_STATUS } from 'components/widgets/modal/constants';

export default {
  name: 'WhatsAppCallUI',
  components: {
    Label,
    MultiselectDropdown,
  },
  props: {
    contactName: { type: String, default: '' },
    phoneNumber: { type: String, default: '' },
    callDurationLabel: { type: String, default: '' },
    selectedStatus: { type: Object, default: () => ({}) },
    statusList: { type: Array, default: () => [] },

    isAcceptBtnShow: { type: Boolean, default: false },
    isRejectBtnShow: { type: Boolean, default: false },
    isEndBtnShow: { type: Boolean, default: false },
    isRedialBtnShow: { type: Boolean, default: false },
    isShowEvaluationOptions: { type: Boolean, default: false },
    isShowCloseBtn: { type: Boolean, default: false },
    isAccepting: { type: Boolean, default: false },
    isRejecting: { type: Boolean, default: false },
    isRedialing: { type: Boolean, default: false },

    // labels
    callStatusText: { type: String, default: '' },
    callStatus: { type: Object, default: () => ({}) },
  },
  computed: {
    formattedDuration() {
      const minutes = Math.floor(this.callDuration / 60);
      const seconds = this.callDuration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    isDialing() {
      return this.callStatus.label === CALL_STATUS.DIALING;
    },
  },
  methods: {
    statusChangeHandle(e) {
      this.$emit('onStatusChange', e);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';
@import '~dashboard/assets/scss/mixins';

.call_container {
  position: relative;
  text-align: center;
}

.close-button {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 32px;
  height: 32px;
  background: white;
  border: 1px solid var(--s-200);
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--s-700);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;

  &:hover {
    background-color: var(--s-50);
    color: var(--s-900);
    border-color: var(--s-300);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transform: scale(1.05);
  }

  &:active {
    background-color: var(--s-100);
    transform: scale(0.95);
  }
}

.status_box {
  max-width: 200px;
  margin: 20px auto 0 auto;
}
</style>
<style lang="scss">
.capitalize {
  text-transform: capitalize;
}

.animated-dots {
  display: inline-block;
  span {
    animation: blink 1.4s infinite;
    opacity: 0;
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes blink {
  0%,
  20% {
    opacity: 0;
  }
  40%,
  60% {
    opacity: 1;
  }
  80%,
  100% {
    opacity: 0;
  }
}
</style>
